/**
 * Beheer > Checkout (checkout 11). Eén scherm voor de eigen checkoutlaag,
 * alleen voor beheerders, zoals de API erachter.
 *
 * Links de schakelaars (noodrem, de tien onderdelen, ordercontrole), rechts
 * de cijfers (trechter, foutmeetpunten, bestandsbewaking, ankers). Onder
 * 900 px één kolom, in dezelfde volgorde. Stand altijd als woord; wat uit
 * staat of afwijkt valt op met een gele vulling, nooit rood.
 *
 * Vervangt Checkout-schakelaars op /beheer/checkout; de testids van de
 * schakelaars zijn gelijk gebleven. Checkout-meting blijft als eigen scherm.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

import { checkoutAdminApi, vertaalApiFout } from "@/lib/api";
import type {
  CheckoutAssetOverzicht,
  CheckoutAssetStand,
  CheckoutLaagstand,
  CheckoutTrechterStap,
} from "@/types/api";

import { CheckoutRegel } from "./CheckoutRegel";

import "./beheer.css";
import "./checkout-beheer.css";

/** Zelfde sleutels als Checkout-meting: één stand in beeld. */
const LAAG = ["admin", "checkoutLaag"] as const;
const ASSETS = ["admin", "checkoutAssets"] as const;
const ORDERCONTROLE = ["admin", "checkoutOrderControle"] as const;

const ONDERDEEL_NAAM: Record<string, string> = {
  kop_voet: "Kop, voortgang en voet",
  teksten: "Je-vorm in de teksten",
  stap1: "Stap 1 in fasen",
  adres: "Adresvelden en factuuradres",
  bezorging: "Bezorgopties per vervoerder",
  offerte: "Offerte onthouden",
  overzicht: "Overzicht",
  betaaldoorstap: "Betaalstap doorzetten",
  stapwissel: "Klikfeedback en wachtstand",
  bedankt: "Bedankpagina en betaalstatus",
  checkout16: "Checkout 16 (nieuwe schermen)",
};

const SIGNAAL_NAAM: Record<string, string> = {
  checkout_scriptfout: "Scriptfouten",
  quote_mislukt: "Offerte mislukt",
  checkout_inlog_fout: "Inlogfout",
};

const datumFmt = new Intl.DateTimeFormat("nl-NL", {
  timeZone: "Europe/Amsterdam",
  day: "numeric",
  month: "long",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});
const kortFmt = new Intl.DateTimeFormat("nl-NL", {
  timeZone: "Europe/Amsterdam",
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});
const dagFmt = new Intl.DateTimeFormat("nl-NL", {
  timeZone: "Europe/Amsterdam",
  day: "numeric",
  month: "short",
});

function opmaak(fmt: Intl.DateTimeFormat, moment: string | null | undefined): string {
  if (!moment) return "onbekend";
  const d = new Date(moment);
  return Number.isNaN(d.getTime()) ? moment : fmt.format(d);
}

function laatstGewijzigd(stand: CheckoutLaagstand): string {
  if (!stand.bijgewerkt_op) return "Nog niet gewijzigd";
  const wanneer = opmaak(datumFmt, stand.bijgewerkt_op);
  return stand.bijgewerkt_door_naam
    ? `Laatst gewijzigd door ${stand.bijgewerkt_door_naam}, ${wanneer}`
    : `Laatst gewijzigd ${wanneer}`;
}

// ── Bouwstenen ──────────────────────────────────────────────────────

function Blok({
  titel,
  testid,
  rechts,
  children,
}: {
  titel: string;
  testid: string;
  rechts?: ReactNode;
  children: ReactNode;
}) {
  const id = `c11-${testid}`;
  return (
    <section className="c11-blok" aria-labelledby={id} data-testid={testid}>
      <header className="c11-blok-kop">
        <h2 id={id}>{titel}</h2>
        {rechts}
      </header>
      {children}
    </section>
  );
}

function Tegel({
  testid,
  waarde,
  label,
  periode,
  letOp = false,
}: {
  testid: string;
  waarde: string | number;
  label: string;
  periode?: string;
  letOp?: boolean;
}) {
  return (
    <div className={`c11-tegel${letOp ? " let-op" : ""}`} data-testid={`tegel-${testid}`}>
      <b>{waarde}</b>
      <span className="c11-tegel-label">{label}</span>
      {periode ? <span className="c11-tegel-periode">{periode}</span> : null}
    </div>
  );
}

function LaadFout({
  wat,
  fout,
  opnieuw,
}: {
  wat: string;
  fout: unknown;
  opnieuw: () => void;
}) {
  return (
    <div className="v6-fout" role="alert">
      {`${wat} laden mislukt. ${vertaalApiFout(fout, "")}`.trim()}{" "}
      <button type="button" className="v6-link" onClick={opnieuw}>
        Opnieuw
      </button>
    </div>
  );
}

// ── Noodrem en onderdelen ───────────────────────────────────────────

function useLaag() {
  return useQuery({ queryKey: LAAG, queryFn: () => checkoutAdminApi.laagstand() });
}

/** Eén schrijfactie tegelijk op de laagrij (noodrem, onderdelen,
 *  ordercontrole): twee tegelijk zouden elkaars stand overschrijven. */
interface LaagSlot {
  bezig: boolean;
  zet: (aan: boolean, onderdeel?: string) => Promise<void>;
}

function useLaagSlot(): LaagSlot {
  const qc = useQueryClient();
  const [bezig, setBezig] = useState(false);
  const zet = async (aan: boolean, onderdeel?: string) => {
    setBezig(true);
    try {
      const nieuw = await checkoutAdminApi.zetLaagstand(aan, onderdeel);
      qc.setQueryData(LAAG, nieuw);
      if (onderdeel === "ordercontrole_live") {
        void qc.invalidateQueries({ queryKey: ORDERCONTROLE });
      }
    } finally {
      setBezig(false);
    }
  };
  return { bezig, zet };
}

function LaagBlokken({ slot }: { slot: LaagSlot }) {
  const stand = useLaag();
  const zet = slot.zet;
  const data = stand.data;
  const laagAan = data?.actief ?? true;
  const uitTal = data ? data.onderdelen.filter((o) => !o.actief).length : 0;

  return (
    <>
      <Blok titel="Noodrem" testid="blok-noodrem">
        {stand.isError ? (
          <LaadFout wat="Stand" fout={stand.error} opnieuw={() => void stand.refetch()} />
        ) : null}
        {stand.isPending ? <p className="v6-bh-leeg">Laden</p> : null}
        {data ? (
          <>
            <ul className="v6-bh-rijen" aria-label="Noodrem">
              <CheckoutRegel
                testid="noodrem"
                naam="Hele checkoutlaag"
                aan={laagAan}
                hoofd
                geblokt={slot.bezig}
                opZet={(aan) => zet(aan)}
              />
            </ul>
            <p className="v6-bh-wie c11-wie" data-testid="laatst-gewijzigd">
              {laatstGewijzigd(data)}
            </p>
          </>
        ) : null}
      </Blok>
      <Blok
        titel={laagAan ? "Onderdelen" : "Onderdelen, laag staat uit"}
        testid="blok-onderdelen"
        rechts={
          uitTal > 0 ? <span className="v6-bh-stand uit">{`${uitTal} uit`}</span> : null
        }
      >
        {data ? (
          <ul className="v6-bh-rijen" aria-label="Onderdelen">
            {data.onderdelen.map((o) => (
              <CheckoutRegel
                key={o.sleutel}
                testid={o.sleutel}
                naam={ONDERDEEL_NAAM[o.sleutel] ?? o.sleutel}
                aan={o.actief}
                geblokt={slot.bezig}
                opZet={(aan) => zet(aan, o.sleutel)}
              />
            ))}
          </ul>
        ) : (
          <p className="v6-bh-leeg">{stand.isPending ? "Laden" : "Geen stand"}</p>
        )}
      </Blok>
    </>
  );
}

// ── Ordercontrole ───────────────────────────────────────────────────

function OrdercontroleBlok({ slot }: { slot: LaagSlot }) {
  const laag = useLaag();
  const tellers = useQuery({
    queryKey: ORDERCONTROLE,
    queryFn: () => checkoutAdminApi.ordercontrole(),
  });
  const zet = (aan: boolean) => slot.zet(aan, "ordercontrole_live");
  const t = tellers.data;
  return (
    <Blok
      titel="Ordercontrole"
      testid="blok-ordercontrole"
      rechts={
        t?.laatste_controle_op ? (
          <span className="c11-klein">{`Laatst ${opmaak(kortFmt, t.laatste_controle_op)}`}</span>
        ) : null
      }
    >
      {laag.isError ? (
        <LaadFout wat="Stand" fout={laag.error} opnieuw={() => void laag.refetch()} />
      ) : null}
      {laag.data ? (
        <ul className="v6-bh-rijen" aria-label="Ordercontrole">
          <CheckoutRegel
            testid="ordercontrole"
            naam="Ordercontrole live"
            aan={laag.data.ordercontrole_live}
            woordAan="Live"
            woordUit="Droog"
            doeAan="live zetten"
            doeUit="op droog zetten"
            opvallend={false}
            vinkje
            geblokt={slot.bezig}
            opZet={zet}
          />
        </ul>
      ) : null}
      {tellers.isError ? (
        <LaadFout wat="Tellers" fout={tellers.error} opnieuw={() => void tellers.refetch()} />
      ) : null}
      {t ? (
        <div className="c11-tegels">
          <Tegel testid="oc-vandaag" waarde={t.vandaag.gecontroleerd} label="Gecontroleerd" periode="vandaag" />
          <Tegel
            testid="oc-vandaag-afwijking"
            waarde={t.vandaag.afwijkingen}
            label="Afwijkingen"
            periode="vandaag"
            letOp={t.vandaag.afwijkingen > 0}
          />
          <Tegel testid="oc-week" waarde={t.week.gecontroleerd} label="Gecontroleerd" periode="deze week" />
          <Tegel
            testid="oc-week-afwijking"
            waarde={t.week.afwijkingen}
            label="Afwijkingen"
            periode="deze week"
            letOp={t.week.afwijkingen > 0}
          />
        </div>
      ) : null}
    </Blok>
  );
}

// ── Trechter en verloop ─────────────────────────────────────────────

type Periode = "dag" | "week" | "maand";
const PERIODES: { sleutel: Periode; woord: string; dagen: number; tekst: string }[] = [
  { sleutel: "dag", woord: "Dag", dagen: 1, tekst: "laatste 24 uur" },
  { sleutel: "week", woord: "Week", dagen: 7, tekst: "laatste 7 dagen" },
  { sleutel: "maand", woord: "30 dagen", dagen: 30, tekst: "laatste 30 dagen" },
];

function sessieCijfer(stappen: CheckoutTrechterStap[], gebeurtenis: string): number {
  const stap = stappen.find((s) => s.gebeurtenis === gebeurtenis);
  if (!stap) return 0;
  return stap.sessies ?? stap.totaal;
}

function TrechterBlok() {
  const [periode, setPeriode] = useState<Periode>("week");
  const keuze = PERIODES.find((p) => p.sleutel === periode) ?? PERIODES[1];
  const bucket = keuze.dagen >= 30 ? "week" : "dag";
  const trechter = useQuery({
    queryKey: ["admin", "checkoutTrechter", keuze.dagen],
    queryFn: () => checkoutAdminApi.trechter(keuze.dagen),
  });
  const verloop = useQuery({
    queryKey: ["admin", "checkoutVerloop", bucket, keuze.dagen],
    queryFn: () => checkoutAdminApi.verloop(bucket, keuze.dagen),
  });

  const stappen = trechter.data?.stappen ?? [];
  const gestart = sessieCijfer(stappen, "checkout_gestart");
  const poging = sessieCijfer(stappen, "betaalpoging");
  const afgerond = sessieCijfer(stappen, "checkout_afgerond");
  const conversie = gestart > 0 ? `${Math.round((100 * afgerond) / gestart)}%` : "-";
  const rijen = verloop.data?.rijen ?? [];
  const betaald = rijen.reduce((som, r) => som + r.orders_betaald, 0);

  return (
    <Blok
      titel="Trechter en verloop"
      testid="blok-trechter"
      rechts={
        <div className="c11-periode" role="group" aria-label="Periode">
          {PERIODES.map((p) => (
            <button
              key={p.sleutel}
              type="button"
              aria-pressed={p.sleutel === periode}
              onClick={() => setPeriode(p.sleutel)}
            >
              {p.woord}
            </button>
          ))}
        </div>
      }
    >
      {trechter.isError ? (
        <LaadFout wat="Trechter" fout={trechter.error} opnieuw={() => void trechter.refetch()} />
      ) : null}
      {trechter.data ? (
        <div className="c11-tegels">
          <Tegel testid="gestart" waarde={gestart} label="Gestart" periode={keuze.tekst} />
          <Tegel testid="betaalpoging" waarde={poging} label="Betaalpoging" periode={keuze.tekst} />
          <Tegel testid="afgerond" waarde={afgerond} label="Afgerond" periode={keuze.tekst} />
          <Tegel testid="conversie" waarde={conversie} label="Conversie" periode={keuze.tekst} />
          {verloop.data ? (
            <Tegel testid="betaald" waarde={betaald} label="Betaalde orders" periode={keuze.tekst} />
          ) : null}
        </div>
      ) : trechter.isPending ? (
        <p className="v6-bh-leeg">Laden</p>
      ) : null}
      {trechter.data?.ondergrens_actief ? (
        <p className="c11-klein c11-regel" data-testid="ondergrens">
          {`Telt vanaf ${opmaak(kortFmt, trechter.data.ondergrens)}`}
        </p>
      ) : null}
      {verloop.isError ? (
        <LaadFout wat="Verloop" fout={verloop.error} opnieuw={() => void verloop.refetch()} />
      ) : null}
      {rijen.length > 0 ? (
        <div className="c11-verloop-schuif">
          {/* Het verloop telt meldingen, geen sessies: daarom geen tweede
              conversiecijfer naast de sessietegels. */}
          <table className="c11-verloop" data-testid="verloop">
            <caption>{`Meldingen per ${bucket === "week" ? "week" : "dag"}`}</caption>
            <thead>
              <tr>
                <th scope="col">{bucket === "week" ? "Week van" : "Dag"}</th>
                <th scope="col">Gestart</th>
                <th scope="col">Betaalpoging</th>
                <th scope="col">Afgerond</th>
                <th scope="col">Betaald</th>
              </tr>
            </thead>
            <tbody>
              {rijen.map((r) => (
                <tr key={r.bucket_start}>
                  <th scope="row">{opmaak(dagFmt, r.bucket_start)}</th>
                  <td>{r.pogingen}</td>
                  <td>{r.betaalpogingen}</td>
                  <td>{r.gelukt}</td>
                  <td>{r.orders_betaald}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Blok>
  );
}

// ── Laatste 7 dagen: foutmeetpunten ─────────────────────────────────

function SignalenBlok() {
  const signalen = useQuery({
    queryKey: ["admin", "checkoutSignalen"],
    queryFn: () => checkoutAdminApi.signalen(),
  });
  const tellers = signalen.data?.tellers ?? [];
  const niets = signalen.data !== undefined && tellers.every((t) => t.totaal === 0);
  return (
    <Blok titel="Laatste 7 dagen" testid="blok-signalen">
      {signalen.isError ? (
        <LaadFout wat="Tellers" fout={signalen.error} opnieuw={() => void signalen.refetch()} />
      ) : null}
      {signalen.isPending ? <p className="v6-bh-leeg">Laden</p> : null}
      {tellers.length > 0 ? (
        <div className="c11-tegels drie">
          {tellers.map((t) => (
            <Tegel
              key={t.gebeurtenis}
              testid={t.gebeurtenis}
              waarde={t.totaal}
              label={SIGNAAL_NAAM[t.gebeurtenis] ?? t.gebeurtenis}
              letOp={t.totaal > 0}
            />
          ))}
        </div>
      ) : null}
      {niets ? (
        <p className="v6-bh-leeg c11-regel" data-testid="signalen-leeg">
          Nog niets gemeten
        </p>
      ) : null}
    </Blok>
  );
}

// ── Bestandsbewaking ────────────────────────────────────────────────

function standWoord(stand: CheckoutAssetStand): { woord: string; valtOp: boolean } {
  switch (stand.gemeten_uitkomst) {
    case "gelijk":
      return { woord: "Gelijk", valtOp: false };
    case "afwijking":
      return { woord: "Afwijking", valtOp: true };
    case "http_fout":
      return { woord: `HTTP ${stand.gemeten_http_status ?? "fout"}`, valtOp: true };
    case "onbereikbaar":
      return { woord: "Geen verbinding", valtOp: true };
    default:
      return { woord: "Niet gemeten", valtOp: false };
  }
}

function sinds(stand: CheckoutAssetStand): string | null {
  if (stand.gemeten_uitkomst === "afwijking") return stand.afwijkt_sinds;
  if (stand.gemeten_uitkomst === "gelijk") return stand.gelijk_sinds;
  return null;
}

function BestandRij({
  stand,
  opGelijk,
}: {
  stand: CheckoutAssetStand;
  opGelijk: (bestand: string) => Promise<void>;
}) {
  const [vraag, setVraag] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const { woord, valtOp } = standWoord(stand);
  const vanaf = sinds(stand);
  const ja = async () => {
    setBezig(true);
    try {
      await opGelijk(stand.bestand);
      setFout(null);
    } catch (e) {
      setFout(`Gelijkzetten mislukt. ${vertaalApiFout(e, "")}`.trim());
    } finally {
      setBezig(false);
      setVraag(false);
    }
  };
  return (
    <li className="c11-bestand" data-testid={`bestand-${stand.bestand}`}>
      <div className="c11-bestand-kop">
        <span className="v6-bh-naam">{stand.bestand}</span>
        <span className={`v6-bh-stand${valtOp ? " uit" : ""}`}>{woord}</span>
      </div>
      <dl className="c11-feiten">
        {vanaf ? (
          <>
            <dt>Sinds</dt>
            <dd>{opmaak(kortFmt, vanaf)}</dd>
          </>
        ) : null}
        <dt>Gemeten</dt>
        <dd>
          {stand.gemeten_op ? opmaak(kortFmt, stand.gemeten_op) : "nog niet"}
          {stand.mislukte_pogingen_op_rij > 1 ? `, ${stand.mislukte_pogingen_op_rij} keer op rij mislukt` : ""}
        </dd>
        <dt>Referentie</dt>
        <dd>
          {opmaak(kortFmt, stand.referentie_gezet_op)}
          {stand.referentie_gezet_door ? `, ${stand.referentie_gezet_door}` : ""}
        </dd>
      </dl>
      {vraag ? (
        <div className="v6-bh-vraag" role="group" aria-label={`Referentie ${stand.bestand} gelijkzetten`}>
          <span>{`Referentie ${stand.bestand} gelijkzetten aan wat nu live staat?`}</span>
          <button
            type="button"
            className="v6-knop acc"
            autoFocus
            disabled={bezig}
            onClick={() => void ja()}
          >
            {bezig ? "Bezig" : "Ja, gelijkzetten"}
          </button>
          <button type="button" className="v6-knop" disabled={bezig} onClick={() => setVraag(false)}>
            Annuleren
          </button>
        </div>
      ) : (
        <div>
          <button
            type="button"
            className="v6-knop zacht c11-klein-knop"
            aria-label={`Referentie ${stand.bestand} gelijkzetten`}
            onClick={() => setVraag(true)}
          >
            Referentie gelijkzetten
          </button>
        </div>
      )}
      {fout ? (
        <div className="v6-fout" role="alert">
          {fout}
        </div>
      ) : null}
    </li>
  );
}

function BewakingBlok() {
  const qc = useQueryClient();
  const assets = useQuery({ queryKey: ASSETS, queryFn: () => checkoutAdminApi.assets() });
  const data = assets.data;
  const gelijk = async (bestand: string) => {
    const nieuw = await checkoutAdminApi.zetAssetReferentie(bestand);
    qc.setQueryData<CheckoutAssetOverzicht>(ASSETS, (oud) =>
      oud
        ? { ...oud, bestanden: oud.bestanden.map((b) => (b.bestand === bestand ? nieuw : b)) }
        : oud,
    );
  };
  const zetSignaal = async (aan: boolean) => {
    const nieuw = await checkoutAdminApi.zetAssetSignaal(aan);
    qc.setQueryData<CheckoutAssetOverzicht>(ASSETS, (oud) =>
      oud ? { ...oud, signaal_aan: nieuw.actief } : oud,
    );
  };
  return (
    <Blok
      titel="Bestandsbewaking"
      testid="blok-bewaking"
      rechts={data && !data.bewaking_aan ? <span className="v6-bh-stand uit">Bewaking uit</span> : null}
    >
      {assets.isError ? (
        <LaadFout wat="Bewaking" fout={assets.error} opnieuw={() => void assets.refetch()} />
      ) : null}
      {assets.isPending ? <p className="v6-bh-leeg">Laden</p> : null}
      {data ? (
        <>
          <ul className="v6-bh-rijen" aria-label="Signaal">
            <CheckoutRegel
              testid="bewaking-signaal"
              naam="Signaal naar Vandaag na 1 uur"
              aan={data.signaal_aan ?? false}
              opvallend={false}
              opZet={zetSignaal}
            />
          </ul>
          {data.bestanden.length === 0 ? (
            <p className="v6-bh-leeg">Geen bewaakte bestanden</p>
          ) : (
            <ul className="c11-lijst" aria-label="Bewaakte bestanden">
              {data.bestanden.map((stand) => (
                <BestandRij key={stand.bestand} stand={stand} opGelijk={gelijk} />
              ))}
            </ul>
          )}
        </>
      ) : null}
    </Blok>
  );
}

// ── Ankers ──────────────────────────────────────────────────────────

function AnkersBlok() {
  const ankers = useQuery({
    queryKey: ["admin", "checkoutAnkers"],
    queryFn: () => checkoutAdminApi.ankers(),
  });
  const rijen = (ankers.data?.rijen ?? [])
    .filter((r) => r.totaal > 0)
    .sort((a, b) => b.totaal - a.totaal);
  return (
    <Blok
      titel="Gemiste ankers"
      testid="blok-ankers"
      rechts={<span className="c11-klein">laatste 7 dagen</span>}
    >
      {ankers.isError ? (
        <LaadFout wat="Ankers" fout={ankers.error} opnieuw={() => void ankers.refetch()} />
      ) : null}
      {ankers.isPending ? <p className="v6-bh-leeg">Laden</p> : null}
      {ankers.data && rijen.length === 0 ? <p className="v6-bh-leeg">Geen gemiste ankers</p> : null}
      {rijen.length > 0 ? (
        <ul className="c11-lijst" aria-label="Gemiste ankers">
          {rijen.map((r) => (
            <li
              key={`${r.onderdeel}-${r.anker}`}
              className="c11-anker"
              data-testid={`anker-${r.onderdeel}-${r.anker}`}
            >
              <span className="c11-anker-naam">
                {`${ONDERDEEL_NAAM[r.onderdeel] ?? r.onderdeel}: ${r.anker}`}
              </span>
              <span className="c11-anker-cijfer">
                <b>{r.totaal}</b> totaal, {r.gisteren} gisteren
              </span>
              {r.boven_drempel ? <span className="v6-bh-stand uit">Boven drempel</span> : null}
              {r.nieuw ? <span className="v6-chip">Nieuw</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </Blok>
  );
}

// ── Het scherm ──────────────────────────────────────────────────────

export function WerkplekV6CheckoutBeheer() {
  const slot = useLaagSlot();
  return (
    <section className="v6-vlak v6-beheer" aria-label="Checkout">
      <header className="v6-bh-kop">
        <h1>Checkout</h1>
      </header>
      <div className="v6-bh-scrol">
        <div className="c11-raster">
          <div className="c11-kolom">
            <LaagBlokken slot={slot} />
            <OrdercontroleBlok slot={slot} />
          </div>
          <div className="c11-kolom">
            <TrechterBlok />
            <SignalenBlok />
            <BewakingBlok />
            <AnkersBlok />
          </div>
        </div>
      </div>
    </section>
  );
}
